import { useState, useEffect, useRef } from 'react';
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowLeft, User, ArrowRight } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { sendPasswordResetEmail, fetchSignInMethodsForEmail, formatAuthError } from '@/lib/firebase/auth';
import { signInWithGoogleWindow, isSignInWindowSupported } from '@/lib/auth/google-signin-window';
import { isEmbeddedIOSWebView } from '@/lib/auth/embedded-webview';

interface InlineLoginFormProps {
  /** Pre-filled email (e.g. suggested CRM email) */
  suggestedEmail?: string;
  /** Hint text shown above the form */
  hint?: string;
  /** SaaS platform label (e.g. "CRM", "helpdesk") — defaults to "CRM" */
  platformLabel?: string;
  /** Called when user clicks cancel (if provided, shows cancel button) */
  onCancel?: () => void;
  /** Called after successful sign-in/sign-up */
  onSuccess?: () => void;
}

/**
 * Compact inline login form for use inside iframes.
 * Avoids navigating to a separate /login page.
 */
export function InlineLoginForm({ suggestedEmail, hint, platformLabel = 'CRM', onCancel, onSuccess }: InlineLoginFormProps) {
  const [email, setEmail] = useState(suggestedEmail || '');

  // Update email when suggestedEmail arrives asynchronously (e.g. from SDK)
  useEffect(() => {
    if (suggestedEmail && !email) setEmail(suggestedEmail);
  }, [suggestedEmail]); // eslint-disable-line react-hooks/exhaustive-deps
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resetError, setResetError] = useState('');
  // Google sign-in cannot complete in an iOS app's web view (e.g. monday.com
  // iOS) — hide it there and let Google-only accounts set a password instead.
  const [googleUnavailable] = useState(isEmbeddedIOSWebView);
  const [setupStatus, setSetupStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

  const signIn = useAuthStore((s) => s.signIn);
  const signUp = useAuthStore((s) => s.signUp);
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const signInWithGoogleAccessToken = useAuthStore((s) => s.signInWithGoogleAccessToken);
  const needsSignUp = useAuthStore((s) => s.needsSignUp);
  const pendingSignUpEmail = useAuthStore((s) => s.pendingSignUpEmail);
  const pendingSignUpPassword = useAuthStore((s) => s.pendingSignUpPassword);
  const authError = useAuthStore((s) => s.error);

  // Step-based flow, mirroring LoginForm:
  //   'email'    → email input + Continue. On submit, probe Firebase for the
  //                provider(s) registered for the email.
  //   'password' → account uses email/password. Show password field (and
  //                Google as secondary if the account has both linked).
  //   'google'   → account only uses Google. Hide password fields entirely.
  // When `suggestedEmail` is provided (CRM-linked auth flow), we auto-probe
  // on mount so the user lands on the correct step without pressing Continue.
  const [step, setStep] = useState<'email' | 'password' | 'google'>('email');
  const [detecting, setDetecting] = useState(false);
  const [methods, setMethods] = useState<string[]>([]);
  const autoProbedRef = useRef(false);

  const probeEmail = async (candidate: string): Promise<'password' | 'google' | 'signup' | null> => {
    const trimmed = candidate.trim();
    if (!trimmed) return null;
    setDetecting(true);
    setLoginError('');
    try {
      const found = await fetchSignInMethodsForEmail(trimmed);
      setMethods(found);
      if (found.length === 0) {
        // No account — route to sign-up with the email pre-filled.
        useAuthStore.setState({
          needsSignUp: true,
          pendingSignUpEmail: trimmed,
          pendingSignUpPassword: '',
          error: null,
        });
        return 'signup';
      }
      if (found.includes('password')) {
        setStep('password');
        return 'password';
      }
      setStep('google');
      return 'google';
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to check email';
      setLoginError(msg.replace('Firebase: ', '').replace(/\(auth\/.*\)/, '').trim());
      return null;
    } finally {
      setDetecting(false);
    }
  };

  // Auto-probe once when we have a suggestedEmail at mount. Prevents an
  // extra click for users arriving from a CRM context that already knows
  // their linked email.
  useEffect(() => {
    if (autoProbedRef.current) return;
    if (!suggestedEmail) return;
    autoProbedRef.current = true;
    probeEmail(suggestedEmail);
  }, [suggestedEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    await probeEmail(email);
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPassword('');
    setMethods([]);
    setLoginError('');
    setSetupStatus('idle');
  };

  // Google-only account where Google sign-in can't run: send the password
  // reset email, which adds a password to the existing account, then show the
  // password step so the user can sign in once they come back.
  const handleSendSetupLink = async () => {
    setSetupStatus('sending');
    setLoginError('');
    try {
      await sendPasswordResetEmail(email.trim());
      setSetupStatus('sent');
      setStep('password');
    } catch (err: unknown) {
      setSetupStatus('idle');
      setLoginError(formatAuthError(err, 'Failed to send the setup link'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setLoginError('');
    try {
      await signIn(email, password);
      onSuccess?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      setLoginError(msg.replace('Firebase: ', '').replace(/\(auth\/.*\)/, '').trim());
    }
    setSubmitting(false);
  };

  const handleGoogleSignIn = async () => {
    setLoginError('');

    // Opened FIRST, before any await. This form renders inside iframes whose
    // host page carries COEP (WhatsApp Web), where Firebase's own popup flow
    // can never deliver its result — so we run sign-in in a window of our own
    // and take the token back over window.opener. window.open needs the
    // click's transient activation, and awaiting anything first spends it.
    // Only where /extension-login is a registered redirect URI; everywhere else
    // the ordinary popup is still correct, and is what works there today.
    let pending: Promise<string> | null = null;
    if (isSignInWindowSupported()) {
      try {
        pending = signInWithGoogleWindow();
      } catch {
        // Window refused (blocked, or origin not configured) — fall through to
        // the popup while some of the click's activation may remain.
        pending = null;
      }
    }

    setSubmitting(true);
    try {
      if (pending) {
        await signInWithGoogleAccessToken(await pending);
      } else {
        // The browser refused the window; the ordinary popup flow is the best
        // remaining option, and it works wherever COEP is not in play.
        await signInWithGoogle();
      }
      onSuccess?.();
    } catch (err: unknown) {
      setLoginError(formatAuthError(err, 'Google sign-in failed'));
    }
    setSubmitting(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    setResetStatus('sending');
    setResetError('');
    try {
      await sendPasswordResetEmail(resetEmail);
      setResetStatus('sent');
    } catch (err: unknown) {
      setResetStatus('error');
      const msg = err instanceof Error ? err.message : 'Failed to send reset email';
      setResetError(msg.replace('Firebase: ', '').replace(/\(auth\/.*\)/, '').trim());
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || !pendingSignUpEmail || !pendingSignUpPassword) return;
    setSubmitting(true);
    setLoginError('');
    try {
      await signUp(pendingSignUpEmail, pendingSignUpPassword, displayName.trim());
      onSuccess?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sign up failed';
      setLoginError(msg.replace('Firebase: ', '').replace(/\(auth\/.*\)/, '').trim());
    }
    setSubmitting(false);
  };

  // ── Sign-up: name + email + password (mirrors login-form.tsx) ──
  if (needsSignUp) {
    return (
      <div className="flex flex-col items-center justify-center p-6 bg-background min-h-[300px]">
        <div className="w-full max-w-xs space-y-4">
          <div className="text-center space-y-1">
            <h2 className="text-base font-semibold text-foreground">Create your account</h2>
            <p className="text-xs text-muted-foreground">
              Enter your details to sign up.
            </p>
          </div>

          {(authError || loginError) && (
            <p className="text-xs text-destructive">{authError || loginError}</p>
          )}

          <form onSubmit={handleSignUp} className="space-y-3">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your full name"
                className="w-full rounded-lg border border-border bg-background py-2.5 pl-10 pr-3 text-sm outline-none focus:ring-1 focus:ring-primary/40"
                autoFocus
                required
              />
            </div>

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="email"
                value={pendingSignUpEmail || ''}
                onChange={(e) => useAuthStore.setState({ pendingSignUpEmail: e.target.value })}
                placeholder="you@example.com"
                className="w-full rounded-lg border border-border bg-background py-2.5 pl-10 pr-3 text-sm outline-none focus:ring-1 focus:ring-primary/40"
                required
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={pendingSignUpPassword || ''}
                onChange={(e) => useAuthStore.setState({ pendingSignUpPassword: e.target.value })}
                placeholder="Create a password"
                className="w-full rounded-lg border border-border bg-background py-2.5 pl-10 pr-10 text-sm outline-none focus:ring-1 focus:ring-primary/40"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            <button
              type="submit"
              disabled={submitting || !displayName.trim() || !pendingSignUpEmail || !pendingSignUpPassword}
              className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Account
            </button>
          </form>

          <button
            type="button"
            onClick={() => useAuthStore.setState({ needsSignUp: false, pendingSignUpEmail: null, pendingSignUpPassword: null, error: null })}
            className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="flex flex-col items-center justify-center p-6 bg-background min-h-[300px]">
        <div className="w-full max-w-xs space-y-4">
          <div className="text-center space-y-1">
            <h2 className="text-base font-semibold text-foreground">Reset password</h2>
            <p className="text-xs text-muted-foreground">
              Enter your email and we'll send a reset link
            </p>
          </div>

          {resetStatus === 'sent' ? (
            <div className="space-y-3">
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-3 py-2.5">
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                  Reset link sent to <strong>{resetEmail}</strong>. Check your inbox.
                </p>
              </div>
              <button
                onClick={() => { setShowForgotPassword(false); setResetStatus('idle'); }}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder={suggestedEmail || "Email"}
                  className="w-full rounded-lg border border-border bg-background py-2.5 pl-10 pr-3 text-sm outline-none focus:ring-1 focus:ring-primary/40"
                  autoFocus
                />
              </div>

              {resetError && (
                <p className="text-xs text-destructive">{resetError}</p>
              )}

              <button
                type="submit"
                disabled={resetStatus === 'sending' || !resetEmail}
                className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {resetStatus === 'sending' ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Send reset link'}
              </button>

              <button
                type="button"
                onClick={() => { setShowForgotPassword(false); setResetStatus('idle'); }}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="inline-login-form" className="flex flex-col items-center justify-center p-6 bg-background min-h-[300px]">
      <div className="w-full max-w-xs space-y-4">
        <div className="text-center space-y-1">
          <h1 className="text-lg font-bold text-foreground bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
            Ulgebra
          </h1>
          <h2 className="text-base font-semibold text-foreground">Sign in / Sign up</h2>
          {hint && !suggestedEmail && (
            <p className="text-xs text-muted-foreground">{hint}</p>
          )}
        </div>

        {/* Info box — when we know the email, tell the user they must use it */}
        {suggestedEmail ? (
          <div className="flex items-start gap-2 rounded-lg bg-primary/5 border border-primary/20 px-3 py-2">
            <Mail className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-foreground leading-relaxed">
              You must sign in with <strong className="text-primary">{suggestedEmail}</strong> — this is the email linked to your {platformLabel} account.
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-lg bg-muted/50 border border-border px-3 py-2">
            <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Use the same email address you use to log in to your {platformLabel}.
            </p>
          </div>
        )}

        {/* Google sign-in — available on every step as a primary alternative */}
        {(() => {
          const GoogleBtn = ({ variant }: { variant: 'primary' | 'secondary' }) => (
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={submitting}
              className={
                variant === 'primary'
                  ? 'w-full flex items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50'
                  : 'w-full flex items-center justify-center gap-2 rounded-lg border border-border bg-background py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50'
              }
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
              )}
              Continue with Google
            </button>
          );

          const EmailPill = (
            <button
              type="button"
              onClick={handleBackToEmail}
              className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors group"
            >
              <span className="flex items-center gap-2 min-w-0">
                <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate">{email}</span>
              </span>
              <span className="text-xs text-muted-foreground group-hover:text-foreground shrink-0">Change</span>
            </button>
          );

          if (step === 'email') {
            return (
              <>
                <form onSubmit={handleEmailContinue} className="space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={suggestedEmail || 'Email'}
                      className="w-full rounded-lg border border-border bg-background py-2.5 pl-10 pr-3 text-sm outline-none focus:ring-1 focus:ring-primary/40"
                      autoFocus
                      required
                    />
                  </div>

                  {loginError && <p className="text-xs text-destructive">{loginError}</p>}

                  <button
                    type="submit"
                    disabled={detecting || !email.trim()}
                    className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {detecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                    Continue
                  </button>
                </form>

                {!googleUnavailable && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-background px-2 text-muted-foreground">or</span>
                      </div>
                    </div>

                    <GoogleBtn variant="secondary" />
                  </>
                )}
              </>
            );
          }

          if (step === 'password') {
            return (
              <>
                {EmailPill}

                {setupStatus === 'sent' && (
                  <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-3 py-2.5">
                    <p className="text-xs text-emerald-700 dark:text-emerald-300">
                      We sent a link to <strong>{email}</strong>. Open it, set a password, then come back and sign in here.
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      className="w-full rounded-lg border border-border bg-background py-2.5 pl-10 pr-10 text-sm outline-none focus:ring-1 focus:ring-primary/40"
                      autoFocus
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {loginError && <p className="text-xs text-destructive">{loginError}</p>}

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => { setResetEmail(email || suggestedEmail || ''); setShowForgotPassword(true); }}
                      className="text-xs text-primary hover:text-primary/80 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || !password}
                    className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Sign in'}
                  </button>
                </form>

                {/* Google only when this account has both providers linked. */}
                {methods.includes('google.com') && !googleUnavailable && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-background px-2 text-muted-foreground">or</span>
                      </div>
                    </div>
                    <GoogleBtn variant="secondary" />
                  </>
                )}
              </>
            );
          }

          // step === 'google', where Google sign-in can't run: offer a password.
          if (googleUnavailable) {
            return (
              <>
                {EmailPill}

                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground leading-relaxed">
                  This account uses Google to sign in, which isn't available in
                  this app. Set a password for it to sign in here — you can keep
                  using Google everywhere else.
                </div>

                {loginError && <p className="text-xs text-destructive">{loginError}</p>}

                <button
                  type="button"
                  onClick={handleSendSetupLink}
                  disabled={setupStatus === 'sending'}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {setupStatus === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Email me a link to set a password
                </button>
              </>
            );
          }

          // step === 'google'
          return (
            <>
              {EmailPill}

              <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground leading-relaxed">
                This account uses Google to sign in. Continue with Google to
                access your Ulgebra workspace.
              </div>

              {loginError && <p className="text-xs text-destructive">{loginError}</p>}

              <GoogleBtn variant="primary" />

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => { setResetEmail(email || suggestedEmail || ''); setShowForgotPassword(true); }}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            </>
          );
        })()}

        {onCancel && (
          <button
            onClick={onCancel}
            className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
